import { useRef, useState, useEffect } from 'react';

import styled from 'styled-components';
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  Bar, Line, ComposedChart, CartesianGrid
} from 'recharts';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import CustomScrollbar from '../../Share/Normal/CustomScrollbar';

export default function RevenueChart(props) {
  const {
    isMonthMode = true,
    periodData = null,
    thisYear,
    thisYearMaxPrice,
    selectedMonth,
    xLabels = null,
    seriesLabels = null,
    hideBar = false,
    hideLine = false,
    valueUnit = ''
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  // 기본 X축 라벨 (1~12월). xLabels prop 전달 시 해당 라벨 배열로 대체 (범용 월별/기간 차트 재사용) //
  const defaultMonthLabels = [
    t('statistics.chart.month1'), t('statistics.chart.month2'), t('statistics.chart.month3'),
    t('statistics.chart.month4'), t('statistics.chart.month5'), t('statistics.chart.month6'),
    t('statistics.chart.month7'), t('statistics.chart.month8'), t('statistics.chart.month9'),
    t('statistics.chart.month10'), t('statistics.chart.month11'), t('statistics.chart.month12')
  ];

  const monthLabels = (Array.isArray(xLabels) && xLabels.length > 0) ? xLabels : defaultMonthLabels;

  const scrollRef = useRef(null);

  const [hoveredBarIndex, setHoveredBarIndex] = useState(-1);

  // 구간 차트 가로 스크롤: 터치패드 상하 스크롤을 가로 스크롤로 변환 //
  useEffect(() => {
    const el = scrollRef.current;

    if (!el) return;

    const handler = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY * 0.5;
    };

    el.addEventListener('wheel', handler, { passive: false });

    return () => el.removeEventListener('wheel', handler);
  }, [isMonthMode, periodData]);

  if (!isMonthMode) return renderPeriodChart();

  if (thisYear.length === 0) return null;

  const prevMaxPrice = Math.max(...thisYear.map(m => m.prevTotalPrice));
  const maxPrice = Math.max(thisYearMaxPrice, prevMaxPrice);
  const maximum = maxPrice > 0 ? Math.ceil(maxPrice * 1.15) : 1;

  // recharts용 데이터 변환 //
  const chartData = thisYear.map((month, index) => ({
    name: monthLabels[index],
    index,
    totalPrice: month.totalPrice,
    barHeight: Math.max(month.totalPrice, 0),
    prevTotalPrice: month.prevTotalPrice,
    prevLineHeight: Math.max(month.prevTotalPrice, 0),
    momPercent: month.momPercent
  }));

  // 위쪽만 둥근 바 렌더링 //
  function RoundedTopBar({ x, y, width, height, index, fill: overrideFill }) {
    if (!height) return null;
    const isHighlight = index === hoveredBarIndex || index === selectedMonth;
    const fill = overrideFill || (isHighlight ? theme.brand500 : isDark? theme.brand900 : theme.brand175);
    const clipId = `bar-clip-${index}`;
    return (
      <g>
        <defs>
          <clipPath id={clipId}>
            <rect x={x} y={y} width={width} height={height} />
          </clipPath>
        </defs>
        <rect x={x} y={y} width={width} height={height + 7} rx={7} ry={7} fill={fill} clipPath={`url(#${clipId})`} />
      </g>
    );
  }

  // 커스텀 툴팁 //
  function CustomTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    const current = data.totalPrice;
    const prev = data.prevTotalPrice;

    if (current === 0 && prev === 0) return null;

    // 증감률 — momPercent 선산출값 있으면(회원 추이, compute에서 램프가드까지 반영) 그대로 표시(전월 대비), 없으면(매출) 점선(전년 동월) 계열과 계산 //
    let percent = null;
    let percentPrefix = '';

    if (data.momPercent !== undefined) {
      percent = data.momPercent;

      if (percent !== null) percentPrefix = t('statistics.chart.previousMonthCompare') + ' ';
    }
    else if (prev > 0) {
      percent = Math.round(((current - prev) / prev) * 100);
    }

    return (
      <TooltipBox $isDark={isDark}>
        <TooltipMonthLabel $bg={theme.buttonFill}>
          <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
            {data.name}
          </TooltipText>
        </TooltipMonthLabel>
        {
          percent !== null &&
          <TooltipText
            fontFamily='NotoSansKR-Medium'
            fontSize={16}
            color={percent >= 0 ? theme.brand500 : theme.subKey}>
            {percentPrefix + (percent >= 0 ? '+' : '') + percent + '%'}
          </TooltipText>
        }
        {
          !hideBar &&
          <TooltipRow>
            <TooltipColorBox backgroundColor={theme.brand500} />
            {
              seriesLabels?.current &&
              <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.brand500}>
                {seriesLabels.current}
              </TooltipText>
            }
            <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.brand500}>
              {current.toLocaleString('ko-KR')}{valueUnit}
            </TooltipText>
          </TooltipRow>
        }
        {
          !hideLine && prev > 0 &&
          <TooltipRow>
            <TooltipColorBox backgroundColor={theme.subText} />
            {
              seriesLabels?.prev &&
              <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>
                {seriesLabels.prev}
              </TooltipText>
            }
            <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>
              {prev.toLocaleString('ko-KR')}{valueUnit}
            </TooltipText>
          </TooltipRow>
        }
      </TooltipBox>
    );
  }

  // 구간 모드 커스텀 툴팁 (월 헤더 + 연도별 색칩·매출 그리드, 3줄 초과 시 열 확장) //
  function CustomPeriodTooltip({ active, payload, lineYears }) {
    if (!active || !payload || !payload.length) return null;

    const row = payload[0].payload;

    // 최신 연도부터 내림차순 표시 //
    const sortedYears = lineYears.slice().sort((a, b) => b - a);

    // 한 열 최대 3줄, 초과 시 열을 늘려 가로로 확장 //
    const columnCount = Math.ceil(sortedYears.length / 3);

    return (
      <TooltipBox $isDark={isDark}>
        <TooltipMonthLabel $bg={theme.buttonFill}>
          <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
            {monthLabels[row.month]}
          </TooltipText>
        </TooltipMonthLabel>
        <TooltipGrid $columnCount={columnCount}>
          {
            sortedYears.map((year) => {
              const color = year === row.year
                ? theme.brand500
                : theme.chartYearPalette[lineYears.indexOf(year) % theme.chartYearPalette.length];

              return (
                <TooltipEntry key={'period-tooltip-' + year}>
                  <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>
                    {String(year).slice(2)}
                  </TooltipText>
                  <TooltipColorBox backgroundColor={color} />
                  <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>
                    {(row['y' + year] || 0).toLocaleString('ko-KR')}
                  </TooltipText>
                </TooltipEntry>
              );
            })
          }
        </TooltipGrid>
      </TooltipBox>
    );
  }

  // 구간 모드 차트 렌더 (실제 개월 막대 + 구간 포함 연도별 다색 라인 + 가로 스크롤) //
  function renderPeriodChart() {
    if (periodData === null) return null;

    const { data, lineYears, maxPrice, monthCount } = periodData;
    const maximum = maxPrice > 0 ? Math.ceil(maxPrice * 1.15) : 1;

    // 12개월은 한 화면(100%)에 고정, 초과분은 비율만큼 넘쳐서 가로 스크롤 //
    const innerWidth = monthCount > 12 ? (monthCount / 12) * 100 : 100;

    return (
      <ChartWrapper>
        <ChartScrollContainer ref={scrollRef}>
          <ChartInner $width={innerWidth}>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart
                data={data}
                margin={{ top: 20, right: 10, left: 10, bottom: 0 }}
                barCategoryGap="20%">
                <CartesianGrid
                  horizontal={true}
                  vertical={false}
                  stroke={theme.line}
                  strokeDasharray="none"
                />
                <XAxis
                  dataKey="name"
                  axisLine={{ stroke: theme.line }}
                  tickLine={false}
                  tick={{ fontFamily: 'NotoSansKR-Medium', fontSize: 14, fill: theme.text }}
                />
                <YAxis
                  domain={[0, maximum]}
                  hide={true}
                />
                <Tooltip
                  content={<CustomPeriodTooltip lineYears={lineYears} />}
                  cursor={false}
                />
                <Bar
                  dataKey="bar"
                  barSize={43}
                  activeBar={false}
                  shape={RoundedTopBar}
                  onMouseEnter={(_, index) => setHoveredBarIndex(index)}
                  onMouseLeave={() => setHoveredBarIndex(-1)}
                />
                {
                  /* 점선(연도별 비교 라인)을 막대 위 레이어로 그리기 위해 Bar보다 나중에 선언 */
                  lineYears.map((year, index) => (
                    <Line
                      key={'period-line-' + year}
                      type="monotone"
                      dataKey={'y' + year}
                      stroke={theme.chartYearPalette[index % theme.chartYearPalette.length]}
                      strokeWidth={2}
                      strokeDasharray="6 3"
                      dot={{ r: 3, fill: theme.chartYearPalette[index % theme.chartYearPalette.length], stroke: 'none' }}
                      activeDot={{ r: 4, fill: theme.chartYearPalette[index % theme.chartYearPalette.length], stroke: 'none' }}
                      isAnimationActive={false}
                    />
                  ))
                }
              </ComposedChart>
            </ResponsiveContainer>
          </ChartInner>
        </ChartScrollContainer>
        <ScrollbarWrapper>
          <CustomScrollbar
            key={data.length + '-' + (data[0]?.name || '')}
            direction="horizontal"
            scrollRef={scrollRef}
            thumbSize={100}
            thumbColor={theme.brand500}
            showTrackAlways={false}
          />
        </ScrollbarWrapper>
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 10, left: 10, bottom: 0 }}
          barCategoryGap="20%">
          <CartesianGrid
            horizontal={true}
            vertical={false}
            stroke={theme.line}
            strokeDasharray="none"
          />
          <XAxis
            dataKey="name"
            axisLine={{ stroke: theme.line }}
            tickLine={false}
            tick={{ fontFamily: 'NotoSansKR-Medium', fontSize: 14, fill: theme.text }}
          />
          <YAxis
            domain={[0, maximum]}
            hide={true}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={false}
          />
          {
            !hideBar &&
            <Bar
              dataKey="barHeight"
              activeBar={false}
              shape={RoundedTopBar}
              onMouseEnter={(_, index) => setHoveredBarIndex(index)}
              onMouseLeave={() => setHoveredBarIndex(-1)}
            />
          }
          {
            !hideLine &&
            <Line
              type="monotone"
              dataKey="prevLineHeight"
              stroke={theme.subText}
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={{ r: 3, fill: theme.subText, stroke: 'none' }}
              activeDot={{ r: 4, fill: theme.subText, stroke: 'none' }}
              isAnimationActive={false}
            />
          }
        </ComposedChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;


  & svg {
    cursor: pointer !important;
  }

  & *:focus,
  & *:focus-visible {
    outline: none;
  }
`;

const ChartScrollContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
`;

const ChartInner = styled.div`
  width: ${props => props.$width}%;
`;

const ScrollbarWrapper = styled.div`
  width: 100%;
  height: 15px;
`;

const TooltipBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 10px;

  gap: 5px;

  border-radius: 5px;
  background-color: ${props => props.theme.background};
  box-shadow: 0px 4px 50px 0px rgba(0, 0, 0, 0.05);

  white-space: nowrap;
`;

const TooltipMonthLabel = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 100%;

  border-radius: 2px;
  background-color: ${props => props.$bg};
`;

const TooltipRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;
`;

const TooltipGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$columnCount}, auto);

  align-items: center;

  gap: 5px 10px;
`;

const TooltipEntry = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;
`;

const TooltipColorBox = styled.div`
  width: 14px;
  height: 14px;

  border-radius: 2px;
  background-color: ${props => props.backgroundColor};
`;

const TooltipText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;